// postApi.ts - 게시글 관련 API 서비스
import { Post, CreatePostRequest } from '../types/post';
import { apiClient } from '.';
import { mockAdminProfileImageUrl } from '../utils/postMockData';

// API 응답 인터페이스
export interface ApiResponse<T> {
  code: number;
  status: string;
  message: string;
  data: T;
}

// 게시글 목록 응답 데이터 (실제 API 응답 구조)
export interface PostListItem {
  post_id: number;
  title: string;
  content: string;
  post_category: string;
  study_category?: string; // 스터디 카테고리 (옵셔널)
  reply_cnt: number;
  view_cnt: number;
  writer_name: string;
  created_at: string;
  lecture_name?: string;
  chapter?: number;
  study_name?: string;
  pinned: boolean;
  writer_profile_image_url?: string;
}

export interface PostListResponse {
  totalElements: number;
  totalPages: number;
  size: number;
  content: PostListItem[];
  number: number;
  sort: {
    empty: boolean;
    sorted: boolean;
    unsorted: boolean;
  };
  numberOfElements: number;
  pageable: {
    offset: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    paged: boolean;
    pageNumber: number;
    pageSize: number;
    unpaged: boolean;
  };
  first: boolean;
  last: boolean;
  empty: boolean;
}

// 게시판 타입
export type BoardType = 'home' | 'lecture' | 'study' | 'information' | 'notice' | 'coffee-chat' | 'help-desk' | 'archive';

// 환경변수에서 API URL 가져오기
const API_BASE_URL = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://medaicampus.kr/gateway';

// JWT 토큰 가져오기 함수
const getAuthToken = (): string | null => {
  return localStorage.getItem('access_token');
};

// HTTP 요청 헤더 생성
const getHeaders = (): HeadersInit => {
  const token = getAuthToken();
  // /// console.log('JWT 토큰 확인:', token ? '토큰 있음' : '토큰 없음', token?.substring(0, 20) + '...');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` }),
  };
};

// 게시글 작성 API
export const createPost = async (postData: CreatePostRequest): Promise<ApiResponse<Post>> => {
  try {
    /// console.log('게시글 작성 요청:', `${API_BASE_URL}/api/posts`, {postData});
    
    const { data: result } = await apiClient.post<ApiResponse<Post>>(
      `/api/posts`,
      postData,
      { withCredentials: true }
    );
    /// console.log('게시글 작성 응답 object:', {result});
    
    return result;
  } catch (error) {
    /// console.error('게시글 작성 실패:', error);
    throw new Error('게시글 작성에 실패했습니다.');
  }
};

// 게시글 수정 API
export const updatePost = async (postId: number, postData: CreatePostRequest): Promise<ApiResponse<Post>> => {
  try {
    /// console.log('게시글 수정 API 요청:', `${API_BASE_URL}/api/posts/${postId}`, {postData});
    
    const { data: result } = await apiClient.put<ApiResponse<Post>>(
      `/api/posts/${postId}`,
      postData,
      { withCredentials: true }
    );
    /// console.log('게시글 수정 API 응답:', {result});
    
    return result;
  } catch (error) {
    /// console.error('게시글 수정 실패:', error);
    throw new Error('게시글 수정에 실패했습니다.');
  }
};

// 게시글 삭제 API
export const deletePost = async (postId: number): Promise<ApiResponse<null>> => {
  try {
    /// console.log('게시글 삭제 요청:', `${API_BASE_URL}/api/posts/${postId}`);
    
    const { data: result } = await apiClient.delete<ApiResponse<null>>(
      `/api/posts/${postId}`,
      { withCredentials: true }
    );
    /// console.log('게시글 삭제 API 응답:', {result});
    
    return result;
  } catch (error) {
    /// console.error('게시글 삭제 실패:', error);
    throw new Error('게시글 삭제에 실패했습니다.');
  }
};

// 이미지 업로드 API
export const uploadImages = async (files: FileList): Promise<ApiResponse<string[]>> => {
  try {
    const formData = new FormData();
    
    // FileList를 배열로 변환하여 추가
    Array.from(files).forEach((file) => {
      formData.append('images', file);
    });

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const { data: result } = await apiClient.post<ApiResponse<string[]>>(
      `/api/posts/attachments`,
      formData,
      {
        withCredentials: true,
        headers, // Authorization만 유지, Content-Type은 axios가 자동 설정
        timeout: 120000, // 대용량 이미지 업로드는 기본 30초로 부족할 수 있음
      }
    );
    return result;
  } catch (error) {
    /// console.error('이미지 업로드 실패:', error);
    throw new Error('이미지 업로드에 실패했습니다.');
  }
};

// 게시글 목록 조회 API (pinned 파라미터 추가)
export const getPostList = async (
  board: BoardType,
  page = 0,
  size = 20,
  lectureCategoryId?: number,
  lectureId?: number,
  chapter?: number,
  studyCategoryId?: number,
  lecturerId?: number,
  pinned?: boolean  // pinned 파라미터 추가
): Promise<ApiResponse<PostListResponse>> => {
  try {
    // 쿼리 파라미터 구성
    const params = new URLSearchParams({
      board,
      page: page.toString(),
      size: size.toString(),
    });

    // 선택적 파라미터 추가
    if (lectureCategoryId !== undefined) {
      params.append('lectureCategoryId', lectureCategoryId.toString());
    }
    if (lectureId !== undefined) {
      params.append('lectureId', lectureId.toString());
    }
    if (chapter !== undefined) {
      params.append('chapter', chapter.toString());
    }
    if (studyCategoryId !== undefined) {
      params.append('studyCategoryId', studyCategoryId.toString());
    }
    if (lecturerId !== undefined) {
      params.append('lecturerId', lecturerId.toString());
    }
    if (pinned !== undefined) {
      params.append('pinned', pinned.toString());
    }

    const url = `${API_BASE_URL}/api/posts/list?${params.toString()}`;
    // /// console.log('게시글 목록 조회 API 요청:', {url, header: getHeaders()});

    const { data: result } = await apiClient.get<ApiResponse<PostListResponse>>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    // /// console.log('게시글 목록 조회 API 응답 데이터:', {result});
    if(result.data?.content?.some(item => item && item.writer_name === '투비닥터')) {
      result.data.content.forEach(item => {
        if(item && item.writer_name === '투비닥터') {
          item.writer_profile_image_url = mockAdminProfileImageUrl;
        }
      });
    }
    return result;
  } catch (error) {
    /// console.error('게시글 목록 조회 실패:', error);
    throw error; // 에러를 다시 throw하여 호출하는 곳에서 처리할 수 있도록
  }
};

// 댓글 생성 요청 데이터
export interface CreateReplyRequest {
  postId: number;
  htmlContent: string;
}

// 댓글 응답 데이터
export interface ReplyResponse {
  reply_id: number;
  content: string;
  writer_name: string;
  reply_created_at: string;
  images: string[];
}

// 댓글 생성 API
export const createReply = async (replyData: CreateReplyRequest): Promise<ApiResponse<ReplyResponse>> => {
  try {
    const formData = new FormData();
    formData.append('postId', replyData.postId.toString());
    formData.append('htmlContent', replyData.htmlContent);

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    /// console.log('댓글 생성 API 요청:', { url: `${API_BASE_URL}/api/reply`, data: {replyData} });

    const { data: result } = await apiClient.post<ApiResponse<ReplyResponse>>(
      `/api/reply`,
      formData,
      { withCredentials: true, headers }
    );
    /// console.log('댓글 생성 API 응답:', {result});
    return result;
  } catch (error) {
    /// console.error('댓글 생성 실패:', error);
    throw error;
  }
};

// 댓글 수정 API
export const updateReply = async (replyId: number, htmlContent: string, postId: number): Promise<ApiResponse<ReplyResponse>> => {
  try {
    const formData = new FormData();
    formData.append('htmlContent', htmlContent);
    formData.append('postId', postId.toString());

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    /// console.log('댓글 수정 API 요청:', { url: `${API_BASE_URL}/api/reply/${replyId}`, content: {htmlContent} });

    const { data: result } = await apiClient.put<ApiResponse<ReplyResponse>>(
      `/api/reply/${replyId}`,
      formData,
      { withCredentials: true, headers }
    );
    /// console.log('댓글 수정 API 응답:', {result});
    return result;
  } catch (error) {
    /// console.error('댓글 수정 실패:', error);
    throw error;
  }
};

// 댓글 삭제 API
export const deleteReply = async (replyId: number): Promise<ApiResponse<null>> => {
  try {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    /// console.log('댓글 삭제 API 요청:', `${API_BASE_URL}/api/reply/${replyId}`);

    const { data: result } = await apiClient.delete<ApiResponse<null>>(
      `/api/reply/${replyId}`,
      { withCredentials: true, headers }
    );
    /// console.log('댓글 삭제 API 응답:', {result});
    return result;
  } catch (error) {
    /// console.error('댓글 삭제 실패:', error);
    throw error;
  }
};

// 댓글 첨부파일 업로드 API
export const uploadReplyAttachments = async (files: FileList): Promise<ApiResponse<string[]>> => {
  try {
    const formData = new FormData();
    
    // FileList를 배열로 변환하여 추가
    Array.from(files).forEach((file) => {
      formData.append('images', file);
    });

    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    /// console.log('댓글 첨부파일 업로드 API 요청:', `${API_BASE_URL}/api/reply/attachments`, {formData});

    const { data: result } = await apiClient.post<ApiResponse<string[]>>(
      `/api/reply/attachments`,
      formData,
      { withCredentials: true, headers }
    );
    /// console.log('댓글 첨부파일 업로드 API 응답:', {result});
    return result;
  } catch (error) {
    /// console.error('댓글 첨부파일 업로드 실패:', error);
    throw error;
  }
};

export const deleteReplyAttachment = async (replyId: number, imageUrls: string[]): Promise<ApiResponse<null>> => {
  try {
    const token = getAuthToken();
    const headers: HeadersInit = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const formData = new FormData();
    imageUrls.forEach(url => {
      formData.append('imageUrls', url);
    });

    const { data: result } = await apiClient.delete<ApiResponse<null>>(
      `/api/reply/${replyId}/attachments`,
      { withCredentials: true, headers, data: formData }
    );
    return result;
  } catch (error) {
    /// console.error('댓글 첨부파일 삭제 실패:', error);
    throw error;
  }
};

// 게시글 상세 조회 API
export const getPostDetail = async (postId: number): Promise<ApiResponse<any>> => {
  try {
    const url = `${API_BASE_URL}/api/posts/${postId}`;
    /// console.log('게시글 상세 조회 API 요청:', {url, header: getHeaders()});
    const { data: result } = await apiClient.get<ApiResponse<any>>(
      url.replace(API_BASE_URL, ''),
      { withCredentials: true }
    );
    /// console.log('게시글 상세 조회 API 응답 데이터:', {result});
    return result;
  } catch (error) {
    /// console.error('게시글 상세 조회 실패:', error);
    throw error;
  }
};
